import { useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { ArrowUp, Brain, ChevronRight, CircleAlert, FlaskConical, RotateCcw, Square, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import type { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { api, isApiError, type ListResponse } from "@/api/client"
import type { AgentDetail } from "@/api/generated/AgentDetail"
import type { RunSummary } from "@/api/generated/RunSummary"
import { describeError } from "@/api/mutation"
import { useSendOnce } from "@/api/queries/agents"
import { runKeys, useRun } from "@/api/queries/runs"
import { Markdown } from "@/components/app/markdown"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { formatCost, formatDuration, formatTokens } from "@/features/runs/format"
import { useRunStream } from "@/features/runs/run-stream"
import { fromToolCallRow, mergeToolCalls, ToolCallTimeline } from "@/features/runs/tool-calls"
import { AgentAvatar } from "@/features/studio/agent-avatar"
import { type AgentFormValues, agentOverrides, dirtyKeys } from "@/features/studio/agent-form"
import { cn } from "@/lib/utils"

export interface PlaygroundTurn {
  id: string
  role: "user" | "assistant"
  text: string
  runUid?: string
  status: "pending" | "done" | "error" | "cancelled"
  error?: string
}

const uid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** Settings that the one-shot playground cannot override (they apply after saving). */
const NOT_OVERRIDABLE: (keyof AgentFormValues)[] = ["reasoning_effort", "max_tokens", "max_steps"]

function AssistantTurn({ turn }: { turn: PlaygroundTurn }) {
  const { t } = useTranslation()
  const live = useRunStream(turn.runUid, !!turn.runUid)
  const detail = useRun(turn.status !== "pending" ? turn.runUid : undefined)
  const pending = turn.status === "pending"
  const calls = mergeToolCalls((detail.data?.tool_calls ?? []).map(fromToolCallRow), live.toolCalls)
  const text = pending ? live.content : turn.text || live.content
  const reasoning = live.reasoning || detail.data?.reasoning || ""
  const d = detail.data

  return (
    <div className="space-y-2" data-testid="playground-reply">
      {reasoning && (
        <Collapsible defaultOpen={false}>
          <CollapsibleTrigger className="group text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs">
            <ChevronRight className="size-3 transition-transform group-data-[panel-open]:rotate-90" />
            <Brain className={cn("size-3.5", pending && !text && "text-primary animate-pulse")} />
            {pending && !text ? t("studio.agents.try.thinking") : t("studio.agents.try.reasoning")}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="text-muted-foreground mt-1.5 max-h-48 overflow-y-auto border-l-2 pl-3 text-xs leading-relaxed whitespace-pre-wrap">
              {reasoning}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
      {calls.length > 0 && <ToolCallTimeline calls={calls} className="text-sm" />}
      {text ? (
        <Markdown size="sm" streaming={pending}>
          {text}
        </Markdown>
      ) : pending ? (
        <div className="text-muted-foreground flex items-center gap-2 text-xs">
          <Spinner className="size-3.5" />
          {live.step ?? t("studio.agents.try.working")}
        </div>
      ) : null}
      {turn.status === "error" && (
        <div className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-lg border px-3 py-2 text-xs">
          <CircleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span className="break-words whitespace-pre-wrap">
            {turn.error || t("studio.agents.try.failed")}
          </span>
        </div>
      )}
      {turn.status === "cancelled" && (
        <p className="text-muted-foreground text-xs italic">{t("studio.agents.try.cancelled")}</p>
      )}
      {!pending && turn.runUid && (
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[0.7rem] tabular-nums">
          {d && (
            <>
              <span>{formatDuration(d.duration_ms)}</span>
              <span>{t("studio.agents.try.tokens", { n: formatTokens(d.total_tokens) })}</span>
              {d.cost != null && <span>{formatCost(d.cost)}</span>}
            </>
          )}
          <Link
            to="/insights/runs"
            search={{ run: turn.runUid }}
            className="hover:text-foreground underline-offset-2 hover:underline"
          >
            {t("studio.agents.try.openRun")}
          </Link>
        </div>
      )}
    </div>
  )
}

/**
 * "Try it": a scratch conversation with the agent using the editor's current
 * (possibly unsaved) settings as per-turn overrides (`POST /chat/send`).
 * The reply streams live by following the run's event stream.
 */
export function Playground({
  agent,
  form,
  onClose,
}: {
  agent: AgentDetail
  form: UseFormReturn<AgentFormValues>
  onClose: () => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const send = useSendOnce()
  const [contextId, setContextId] = useState(uid)
  const [turns, setTurns] = useState<PlaygroundTurn[]>([])
  const [input, setInput] = useState("")
  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const pending = turns.find((x) => x.status === "pending")
  const dirty = dirtyKeys(form.formState.dirtyFields)
  const ignored = dirty.filter((k) => NOT_OVERRIDABLE.includes(k))
  const toolSwitchesChanged =
    dirty.includes("mcp_servers") && form.getValues("mcp_servers").some((m) => m.disabled_tools.length > 0)

  const patch = useCallback(
    (id: string, p: Partial<PlaygroundTurn>) =>
      setTurns((ts) => ts.map((x) => (x.id === id ? { ...x, ...p } : x))),
    [],
  )

  // keep the newest message in view unless the user scrolled up
  useEffect(() => {
    const el = scrollRef.current
    if (el && pinned.current) el.scrollTop = el.scrollHeight
  })

  useEffect(() => () => abortRef.current?.abort(), [])

  const reset = () => {
    abortRef.current?.abort()
    setTurns([])
    setContextId(uid())
    inputRef.current?.focus()
  }

  const cancel = () => {
    abortRef.current?.abort()
    if (pending?.runUid) void api.post(`/runs/${pending.runUid}/cancel`).catch(() => undefined)
  }

  const submit = async (text?: string) => {
    const message = (text ?? input).trim()
    if (!message || pending) return
    if (!(await form.trigger())) {
      setTurns((ts) => [
        ...ts,
        { id: uid(), role: "assistant", text: "", status: "error", error: t("studio.agents.try.fixErrors") },
      ])
      return
    }
    const values = form.getValues()
    const overrides = agentOverrides(values, form.formState.dirtyFields)
    const known = new Set(turns.map((x) => x.runUid).filter(Boolean))
    const reply: PlaygroundTurn = { id: uid(), role: "assistant", text: "", status: "pending" }
    setTurns((ts) => [...ts, { id: uid(), role: "user", text: message, status: "done" }, reply])
    setInput("")
    pinned.current = true
    const ctrl = new AbortController()
    abortRef.current = ctrl
    const started = Date.now()
    let settled = false

    // `/chat/send` answers only when the run is finished; find the run meanwhile to stream it live
    void (async () => {
      for (let i = 0; i < 80 && !settled && !ctrl.signal.aborted; i++) {
        await sleep(i < 6 ? 200 : 600)
        if (settled || ctrl.signal.aborted) return
        try {
          const res = await api.get<ListResponse<RunSummary>>("/runs", {
            query: { context_id: contextId, limit: 1 },
            signal: ctrl.signal,
          })
          const run = res.items[0]
          if (run && !known.has(run.uid) && Date.parse(run.started_at) >= started - 10_000) {
            if (!settled) patch(reply.id, { runUid: run.uid })
            return
          }
        } catch {
          return
        }
      }
    })()

    try {
      const res = await send.mutateAsync({
        agent_id: agent.id,
        message,
        context_id: contextId,
        overrides,
        signal: ctrl.signal,
      })
      settled = true
      patch(reply.id, {
        runUid: res.run_uid,
        text: res.output,
        status:
          res.status === "cancelled" ? "cancelled" : res.error || res.status === "failed" ? "error" : "done",
        error: res.error ?? undefined,
      })
    } catch (e) {
      settled = true
      const aborted = (e instanceof DOMException && e.name === "AbortError") || ctrl.signal.aborted
      patch(reply.id, {
        status: aborted ? "cancelled" : "error",
        error: aborted ? undefined : isApiError(e) ? e.message || describeError(e, t) : describeError(e, t),
      })
    } finally {
      if (abortRef.current === ctrl) abortRef.current = null
      void qc.invalidateQueries({ queryKey: runKeys.all })
    }
  }

  const starters = [
    t("studio.agents.try.starters.intro"),
    t("studio.agents.try.starters.capabilities"),
    t("studio.agents.try.starters.tools"),
  ]

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="playground">
      <div className="flex items-center gap-2 border-b px-3 py-2.5">
        <FlaskConical className="text-primary size-4" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{t("studio.agents.try.title")}</div>
        </div>
        {dirty.length > 0 && (
          <Tooltip>
            <TooltipTrigger render={<span />}>
              <StatusBadge tone="info" dot>
                {t("studio.agents.try.unsaved")}
              </StatusBadge>
            </TooltipTrigger>
            <TooltipContent className="max-w-64">{t("studio.agents.try.unsavedHint")}</TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={reset}
                disabled={turns.length === 0}
                aria-label={t("studio.agents.try.reset")}
              />
            }
          >
            <RotateCcw />
          </TooltipTrigger>
          <TooltipContent>{t("studio.agents.try.reset")}</TooltipContent>
        </Tooltip>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          aria-label={t("studio.agents.try.close")}
        >
          <X />
        </Button>
      </div>

      <div
        ref={scrollRef}
        onScroll={(e) => {
          const el = e.currentTarget
          pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
        }}
        className="min-h-0 flex-1 overflow-y-auto px-3 py-4"
      >
        {turns.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 px-2 text-center">
            <AgentAvatar name={agent.name} icon={form.getValues("icon")} size="lg" />
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {t("studio.agents.try.emptyTitle", { name: form.getValues("name") || agent.name })}
              </p>
              <p className="text-muted-foreground text-xs text-pretty">
                {t("studio.agents.try.description")}
              </p>
            </div>
            <div className="flex w-full flex-col gap-1.5">
              {starters.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => void submit(s)}
                  className="hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg border px-3 py-2 text-left text-xs transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {turns.map((turn) =>
              turn.role === "user" ? (
                <div key={turn.id} className="flex justify-end">
                  <div className="bg-primary text-primary-foreground max-w-[85%] rounded-2xl rounded-br-md px-3 py-2 text-sm whitespace-pre-wrap break-words">
                    {turn.text}
                  </div>
                </div>
              ) : (
                <AssistantTurn key={turn.id} turn={turn} />
              ),
            )}
          </div>
        )}
      </div>

      {(ignored.length > 0 || toolSwitchesChanged) && (
        <p className="text-muted-foreground border-t px-3 py-1.5 text-[0.7rem]">
          {t("studio.agents.try.ignored")}
        </p>
      )}
      <div className="border-t p-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))]">
        <div className="border-input focus-within:border-ring focus-within:ring-ring/50 dark:bg-input/30 flex items-end gap-2 rounded-xl border p-1.5 pl-3 transition-colors focus-within:ring-3">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault()
                void submit()
              }
            }}
            rows={1}
            placeholder={t("studio.agents.try.placeholder")}
            aria-label={t("studio.agents.try.placeholder")}
            className="max-h-40 min-h-8 resize-none border-0 bg-transparent p-0 py-1 shadow-none focus-visible:ring-0 dark:bg-transparent"
            data-testid="playground-input"
          />
          {pending ? (
            <Button
              type="button"
              size="icon-sm"
              variant="secondary"
              onClick={cancel}
              aria-label={t("studio.agents.try.stop")}
            >
              <Square className="fill-current" />
            </Button>
          ) : (
            <Button
              type="button"
              size="icon-sm"
              onClick={() => void submit()}
              disabled={!input.trim()}
              aria-label={t("studio.agents.try.send")}
              data-testid="playground-send"
            >
              <ArrowUp />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
